// @ts-check

/**
 * @file Renders the account detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import {Account} from "../../../src/frontend-models/account"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import errorMessage from "../../../src/shared/error-message"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"
import Text from "../../../src/components/text"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} AccountsShowScreenProps */

/**
 * @typedef {object} AccountsShowScreenState
 * @property {Account | null} account Loaded account record.
 * @property {boolean} loading Whether account details are loading.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 */

/**
 * Account show screen.
 * @augments {ShapeComponent<AccountsShowScreenProps, AccountsShowScreenState>}
 */
class AccountsShowScreen extends ShapeComponent {
  /** @type {string} */
  accountID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {AccountsShowScreenState} */
  state = {
    account: /** @type {Account | null} */ null,
    loading: true,
    status: /** @type {{kind: "error", text: string} | null} */ null
  }

  /** Initializes route params and account loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()

    const accountID = requiredRouteParam(params, "id", "Account ID")
    this.accountID = accountID
    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const account = await Account.find(accountID)
          if (cancelled) return
          this.s.account = account
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [accountID])
  }

  /**
   * Renders account details.
   * @returns {ReactNode} Account detail content.
   */
  render() {
    const {account, loading, status} = this.s
    const {accountID, params} = this.tt
    const screenTitle = account ? account.name() : _("Account")
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="accountShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="accountShowScreen"
      >
        <ScreenTitle>{screenTitle}</ScreenTitle>
        <View
          testID="accountShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="accountShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {account &&
            <Card
              style={styles.cardSpacing ||= {padding: 20}}
              testID="accountShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="accountShowScreen/headerRow"
              >
                <Text
                  style={styles[`headerTitle-${this.tt.themeName}`] ||= {
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="accountShowScreen/title"
                >
                  {account.name()}
                </Text>
                <Button
                  href={buildRoutePath(params, `/accounts/${accountID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID="accountShowScreen/editButton"
                />
              </View>
              {detailRow({
                colors: this.tt.colors,
                label: _("Name"),
                testID: "name",
                themeName: this.tt.themeName,
                value: account.name() || "-"
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Created"),
                testID: "createdAt",
                themeName: this.tt.themeName,
                value: formatDateTime(account.createdAt())
              })}
              {detailRow({
                colors: this.tt.colors,
                label: _("Updated"),
                testID: "updatedAt",
                themeName: this.tt.themeName,
                value: formatDateTime(account.updatedAt())
              })}
            </Card>
          }
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<AccountsShowScreenProps>} */
const AccountsShowScreenComponent = memo(shapeComponent(AccountsShowScreen))

export default AccountsShowScreenComponent

/**
 * Renders one account detail row.
 * @param {{colors: import("../../../src/shared/theme").BoardThemeColors, label: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Rendered metadata row.
 */
function detailRow({colors, label, testID, themeName, value}) {
  return (
    <View
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`accountShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`accountShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      <Text
        style={styles[`detailValue-${themeName}`] ||= {fontSize: 16}}
        testID={`accountShowScreen/${testID}/value`}
      >
        {value}
      </Text>
    </View>
  )
}

/**
 * Formats a date-time value for display.
 * @param {Date | string | null | undefined} value Date value to format.
 * @returns {string} Formatted date-time text.
 */
function formatDateTime(value) {
  if (!value) return "-"
  const parsed = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString("en")
}
